import { supabase } from "@/integrations/supabase/client";
import type { FeaturedFanRow, PredictionScoreRow } from "./reputationLevels";

/**
 * Reads the current user's prediction reputation row (RLS-scoped to auth.uid()).
 * Returns null when the user is anonymous or has no row yet.
 */
export async function fetchMyReputation(): Promise<PredictionScoreRow | null> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return null;
  const { data, error } = await supabase
    .from("prediction_scores")
    .select("user_id,total_points,exact_predictions,predictions_total,streak_current,streak_best,last_prediction_at")
    .eq("user_id", auth.user.id)
    .maybeSingle();
  if (error) {
    console.warn("[reputation] fetch failed", error.message);
    return null;
  }
  return (data as PredictionScoreRow | null) ?? null;
}

/**
 * Public ranking via SECURITY DEFINER function — exposes only safe display
 * fields (no emails). Anyone (even anon) can fetch.
 */
export async function fetchFeaturedFans(limit = 10): Promise<FeaturedFanRow[]> {
  const { data, error } = await supabase.rpc("get_featured_fans", { _limit: limit });
  if (error) {
    console.warn("[featured-fans] fetch failed", error.message);
    return [];
  }
  return (data as FeaturedFanRow[]) ?? [];
}

/**
 * Increments the signed-in user's reputation row after a prediction is settled.
 * - `pointsDelta`: points won this prediction (0 = wrong, 1/3/5 typical scoring)
 * - `wasExact`: true if score was exact
 * - `correct`: true if at least the outcome was right (used to grow streak)
 */
export async function bumpMyReputation(opts: {
  pointsDelta: number;
  wasExact: boolean;
  correct: boolean;
}): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return;
  const uid = auth.user.id;
  // Read-modify-write under RLS. Safe enough at fan scale; concurrent
  // prediction settles for the same user are rare.
  const { data: existing } = await supabase
    .from("prediction_scores")
    .select("*")
    .eq("user_id", uid)
    .maybeSingle();
  const row = (existing as PredictionScoreRow | null) ?? {
    user_id: uid,
    total_points: 0,
    exact_predictions: 0,
    predictions_total: 0,
    streak_current: 0,
    streak_best: 0,
    last_prediction_at: null,
  };
  const streak_current = opts.correct ? row.streak_current + 1 : 0;
  const streak_best = Math.max(row.streak_best, streak_current);
  const payload = {
    user_id: uid,
    total_points: row.total_points + Math.max(0, opts.pointsDelta),
    exact_predictions: row.exact_predictions + (opts.wasExact ? 1 : 0),
    predictions_total: row.predictions_total + 1,
    streak_current,
    streak_best,
    last_prediction_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from("prediction_scores")
    .upsert(payload, { onConflict: "user_id" });
  if (error) console.warn("[reputation] bump failed", error.message);
}